import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import DraggableBox from "../Draggable/Draggable";
import ModalComponent from "../Modal/Modal";
import { I18nContext } from "../../store/context/i18nContext";

const TAB_KEYS = {
  columns: "columns",
  filters: "filters",
};

const emptyFilterPreferences = {
  hiddenFilters: [],
  defaultFilters: [],
  renamedFilters: {},
};

const normalizePreferenceId = (value = "", fallback = "item") => {
  const normalized = String(value || "")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return normalized || fallback;
};

const ensureStableId = (item = {}, index = 0, prefix = "column") => ({
  ...item,
  id: item.id || item.stableId || `${prefix}-${normalizePreferenceId(item.accessor || item.title, `item-${index + 1}`)}`,
});

const buildColumnDragData = (data = [], value = []) => {
  const visibleItems = (value || []).map((item, index) => ({
    ...ensureStableId(item, index, "visible"),
    subtitle: item.title,
  }));

  const hiddenItems = (data || [])
    .filter((item) => !visibleItems.some((selectedItem) => selectedItem.title === item.title))
    .map((item, index) => ({
      ...ensureStableId(item, index, "hidden"),
      subtitle: item.title,
    }));

  return {
    "Visible Fields": {
      title: "Visible Fields",
      items: visibleItems,
    },
    "Hidden Fields": {
      title: "Hidden Fields",
      items: hiddenItems,
    },
  };
};

function DataDridUIPreferences({
  data = [],
  isOpen,
  onClose,
  value = [],
  onSave,
  onChange,
  title = "Select Columns",
  filterConfig = null,
  testId = "data-grid-preferences",
}) {
  const { t } = useContext(I18nContext);
  const hasFilterConfig = Boolean(
    filterConfig?.enabled &&
    filterConfig?.canConfigure &&
    Array.isArray(filterConfig?.structure)
  );
  const [dragData, setDragData] = useState(buildColumnDragData(data, value));
  const [activeTab, setActiveTab] = useState(TAB_KEYS.columns);
  const [saving, setSaving] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const [localFilterPreferences, setLocalFilterPreferences] = useState(emptyFilterPreferences);

  useEffect(() => {
    setDragData(buildColumnDragData(data, value));
  }, [data, value]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(TAB_KEYS.columns);
    setFilterSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (!hasFilterConfig) {
      setLocalFilterPreferences(emptyFilterPreferences);
      return;
    }

    setLocalFilterPreferences({
      hiddenFilters: [...(filterConfig?.preferences?.hiddenFilters || [])],
      defaultFilters: [...(filterConfig?.preferences?.defaultFilters || [])],
      renamedFilters: { ...(filterConfig?.preferences?.renamedFilters || {}) },
    });
  }, [filterConfig?.preferences, hasFilterConfig]);

  const configurableFilters = useMemo(() => {
    if (!hasFilterConfig) return [];

    return (filterConfig?.structure || [])
      .filter((item) => item?.filterName)
      .map((item) => ({
        ...item,
        originalLabel: item?.filterLabel ?? item?.label ?? item?.filterName,
      }))
      .sort((firstItem, secondItem) => (
        (firstItem?.originalLabel || "").localeCompare(secondItem?.originalLabel || "")
      ));
  }, [filterConfig?.structure, hasFilterConfig]);

  const visibleFilters = useMemo(() => {
    const normalizedSearch = String(filterSearch || "").trim().toLowerCase();
    if (!normalizedSearch) return configurableFilters;

    return configurableFilters.filter((item) => {
      const filterText = [
        item?.originalLabel,
        item?.label,
        item?.filterName,
        item?.description,
      ].filter(Boolean).join(" ").toLowerCase();

      return normalizedSearch
        .split(/\s+/)
        .filter(Boolean)
        .every((token) => filterText.includes(token));
    });
  }, [configurableFilters, filterSearch]);

  const handleChange = (nextDragData) => {
    setDragData(nextDragData);
    onChange?.(nextDragData?.["Visible Fields"]?.items || []);
  };

  const onToggleFilterMenuVisibility = (filterName) => {
    setLocalFilterPreferences((prev) => {
      const hiddenSet = new Set(prev.hiddenFilters || []);
      const defaultSet = new Set(prev.defaultFilters || []);

      if (hiddenSet.has(filterName)) {
        hiddenSet.delete(filterName);
      } else {
        hiddenSet.add(filterName);
        defaultSet.delete(filterName);
      }

      return {
        ...prev,
        hiddenFilters: Array.from(hiddenSet),
        defaultFilters: Array.from(defaultSet),
      };
    });
  };

  const onToggleDefaultFilter = (filterName) => {
    setLocalFilterPreferences((prev) => {
      const hiddenSet = new Set(prev.hiddenFilters || []);
      const defaultSet = new Set(prev.defaultFilters || []);

      if (defaultSet.has(filterName)) {
        defaultSet.delete(filterName);
      } else {
        defaultSet.add(filterName);
        hiddenSet.delete(filterName);
      }

      return {
        ...prev,
        hiddenFilters: Array.from(hiddenSet),
        defaultFilters: Array.from(defaultSet),
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const visibleColumns = dragData["Visible Fields"]?.items || [];
      if (hasFilterConfig) {
        await onSave?.({
          visibleColumns,
          filterPreferences: localFilterPreferences,
          activeTab,
        });
      } else {
        await onSave?.(visibleColumns);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalComponent
      openFrom="right"
      position="centered"
      size="medium"
      onToggle={onClose}
      modalSizeStyle={{ width: "min(760px, 100vw)", maxWidth: "760px", borderRadius: "8px" }}
      isOpen={isOpen}
    >
      <ModalComponent.Title>
        <span data-testid={`${testId}-title`}>{t(title)}</span>
      </ModalComponent.Title>

      {hasFilterConfig ? (
        <div className="zr-grid-pref-tabs" role="tablist" aria-label={t("Grid settings sections")}>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_KEYS.columns}
            className={`zr-grid-pref-tab${activeTab === TAB_KEYS.columns ? " zr-grid-pref-tab--active" : ""}`}
            onClick={() => setActiveTab(TAB_KEYS.columns)}
          >
            {t("Select Columns")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_KEYS.filters}
            className={`zr-grid-pref-tab${activeTab === TAB_KEYS.filters ? " zr-grid-pref-tab--active" : ""}`}
            onClick={() => setActiveTab(TAB_KEYS.filters)}
          >
            {t("Filter Configuration")}
          </button>
        </div>
      ) : null}

      <ModalComponent.Body>
        {activeTab === TAB_KEYS.columns || !hasFilterConfig ? (
          <div data-testid={`${testId}-body`}>
            <DraggableBox value={dragData} onChange={handleChange} showArrows />
          </div>
        ) : (
          <div className="zr-grid-pref-filter-config">
            <div className="zr-grid-pref-filter-toolbar">
              <input
                type="text"
                className="zr-grid-pref-filter-search"
                placeholder={t("Search filters")}
                value={filterSearch}
                onChange={(event) => setFilterSearch(event.target.value)}
              />
              <div className="zr-grid-pref-filter-hint">
                {t("Default filters are always visible in the add-filter menu.")}
              </div>
            </div>

            {filterConfig?.loading ? (
              <div className="zr-grid-pref-filter-skeleton">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div key={item} className="zr-grid-pref-filter-skeleton-row" />
                ))}
              </div>
            ) : visibleFilters.length ? (
              <div className="zr-grid-pref-filter-list">
                <div className="zr-grid-pref-filter-row zr-grid-pref-filter-row--header">
                  <div>{t("Filter")}</div>
                  <div>{t("Show In Menu")}</div>
                  <div>{t("Default")}</div>
                </div>

                {visibleFilters.map((filter) => {
                  const filterName = filter.filterName;
                  const originalLabel = filter.originalLabel;
                  const isAlwaysVisible = Boolean(filter?.showAlways);
                  const showInMenu = !localFilterPreferences.hiddenFilters?.includes(filterName);
                  const isDefaultSelected = isAlwaysVisible || localFilterPreferences.defaultFilters?.includes(filterName);

                  return (
                    <div key={filterName} className="zr-grid-pref-filter-row">
                      <div className="zr-grid-pref-filter-meta">
                        <div className="zr-grid-pref-filter-label-wrap">
                          {isAlwaysVisible ? (
                            <span className="zr-grid-pref-filter-badge">{t("Always")}</span>
                          ) : null}
                          <div className="zr-grid-pref-filter-label-text">{t(originalLabel)}</div>
                        </div>
                      </div>
                      <label className="zr-grid-pref-filter-checkbox">
                        <input
                          type="checkbox"
                          checked={showInMenu}
                          disabled={isAlwaysVisible}
                          onChange={() => onToggleFilterMenuVisibility(filterName)}
                        />
                      </label>
                      <label className="zr-grid-pref-filter-checkbox">
                        <input
                          type="checkbox"
                          checked={isDefaultSelected}
                          disabled={isAlwaysVisible}
                          onChange={() => onToggleDefaultFilter(filterName)}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="zr-grid-pref-filter-empty">{t("No filters found.")}</div>
            )}
          </div>
        )}
      </ModalComponent.Body>

      <ModalComponent.Footer>
        <Button
          type="button"
          color="primary"
          className="mx-2"
          onClick={handleSave}
          disabled={saving || filterConfig?.loading}
          data-testid={`${testId}-confirm`}
        >
          {saving || filterConfig?.saving ? t("Saving...") : t("Save")}
        </Button>
      </ModalComponent.Footer>
    </ModalComponent>
  );
}

export default DataDridUIPreferences;
